import { useState } from 'react';
import { toast } from 'react-toastify';

import { QueryObserverResult } from '@tanstack/react-query';

import { SelfRewardPoolAccessPolicy } from '@shared/api/types/rewardPools';
import { AuthWeb3Modal } from '@shared/common/components/modals/AuthWeb3Modal';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { MarkCheck } from '@ui/uikit/components/icons/mono';

import {
  mapAccessPoliciesToRequirements,
  RequirementItem,
} from '../../../../model/mapAccessPoliciesToRequirements';
import { ListItemButton } from '../../List/ListItemButton';

type Props = {
  requirement: RequirementItem;
  refetch: () => Promise<QueryObserverResult<SelfRewardPoolAccessPolicy[], Error>>;
};

export const VerifyRequirementButton = ({ requirement, refetch }: Props) => {
  const { isAuth } = useAuth();
  const [isOpenEVMModal, setIsOpenEVMModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = (requirementId: string) => async () => {
    setIsVerifying(true);

    if (!isAuth) {
      setIsOpenEVMModal(true);
      return;
    }

    const { data: policies = [] } = await refetch();
    const newRequirements = mapAccessPoliciesToRequirements(policies);
    const currentRequirement = newRequirements.find((req) => req.id === requirementId);

    if (!currentRequirement?.qualified) {
      toast.error(
        'This task cannot be verified. Please make sure you have completed the task correctly and try again.',
      );
    }

    setIsVerifying(false);
  };

  const onCloseModalHandler = async () => {
    setIsVerifying(false);
    setIsOpenEVMModal(false);
  };

  return (
    <>
      {requirement.qualified ? (
        <ListItemButton variant="secondary" disabled={true} data-qa="verify-button">
          <MarkCheck className="w-4 h-4 text-base-text-green" />
        </ListItemButton>
      ) : (
        <ListItemButton
          disabled={isVerifying}
          onClick={handleVerify(requirement.id)}
          isLoading={isVerifying}
          data-qa="verify-button"
        >
          Verify
        </ListItemButton>
      )}
      <AuthWeb3Modal
        isOpen={isOpenEVMModal}
        setIsOpen={setIsOpenEVMModal}
        onClose={onCloseModalHandler}
      />
    </>
  );
};
